import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, ShoppingBag, User } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const BroadcastEnded = () => {
  const { broadcastId } = useParams();
  const navigate = useNavigate();
  const [live, setLive] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/broadcasts/${broadcastId}`);
        if (!cancelled) setLive(res.data);
      } catch { if (!cancelled) setNotFound(true); }
    })();
    return () => { cancelled = true; };
  }, [broadcastId]);

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="broadcast-ended-notfound">
        <p className="text-2xl font-black mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>방송을 찾을 수 없습니다</p>
        <Link to="/schedule" className="text-sm text-[#FF3B30] font-bold hover:underline">전체 편성표 보기</Link>
      </div>
    );
  }
  if (!live) return <div className="max-w-2xl mx-auto px-4 py-24 text-sm text-gray-400 text-center">불러오는 중…</div>;

  const sellerName = live.seller?.name || live.seller_name;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="broadcast-ended-page">
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-black dark:hover:text-white mb-6" data-testid="ended-back">
        <ArrowLeft size={14} /> 뒤로
      </button>

      <div className="text-center mb-8" data-testid="ended-status">
        <span className="inline-block px-3 py-1 rounded-full bg-gray-500/10 text-gray-500 text-xs font-black tracking-widest uppercase">Ended</span>
        <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>방송이 종료되었습니다</h1>
      </div>

      <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 mb-6 grayscale" data-testid="ended-thumb">
        <SafeImg src={live.home_schedule_image_url || live.thumbnail} kind="broadcast" alt={live.title} className="w-full h-full object-cover" />
      </div>

      <div className="space-y-1 mb-8 text-center">
        <p className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="ended-title">{live.title}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="ended-seller">{sellerName}</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Link to={`/seller/${live.seller_id}`} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-black dark:bg-white text-white dark:text-black text-sm font-bold hover:bg-[#FF3B30] hover:text-white transition-colors" data-testid="ended-goto-seller">
          <User size={14} /> 셀러 프로필 가기
        </Link>
        <Link to={`/seller/${live.seller_id}#products`} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-black/10 dark:border-white/10 text-sm font-bold hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors" data-testid="ended-goto-products">
          <ShoppingBag size={14} /> 판매자 전체 상품 보기
        </Link>
        <Link to="/schedule" className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold" data-testid="ended-schedule">
          <Calendar size={14} /> 전체 편성표 보기
        </Link>
      </div>
    </div>
  );
};

export default BroadcastEnded;
